import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Icon from "@/components/Icon";
import TimezoneSelect from "@/components/TimezoneSelect";

const inputCls = "w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary";

const ENTITY_TYPES = [
  "Startup",
  "Small Business / SME",
  "Agency",
  "Freelancer",
  "Consultant",
  "Professional / Individual",
  "Nonprofit / Social Enterprise",
  "Other",
];

export default function OrgSettings() {
  const { user } = useAuth();
  const ctx = useOutletContext() || {};
  const [org, setOrg] = useState(ctx.org || null);
  const [name, setName] = useState(ctx.org?.name || "");
  const [currency, setCurrency] = useState(ctx.org?.default_currency || "USD");
  const [saving, setSaving] = useState(false);
  const [context, setContext] = useState(ctx.org?.business_context || "");
  const [savingCtx, setSavingCtx] = useState(false);
  const [profile, setProfile] = useState(ctx.org?.business_profile || {});
  const [savingProfile, setSavingProfile] = useState(false);
  const isOwner = user?.role === "owner";

  useEffect(() => {
    if (!org) {
      api.get("/organizations/me").then(({ data }) => {
        setOrg(data.organization);
        setName(data.organization.name);
        setCurrency(data.organization.default_currency || "USD");
        setContext(data.organization.business_context || "");
        setProfile(data.organization.business_profile || {});
      }).catch(() => {});
    }
  }, [org]);

  const updateProfile = (patch) => setProfile((p) => ({ ...p, ...patch }));

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const payload = {
        timezone: profile.timezone,
        entity_type: profile.entity_type,
        industry: profile.industry,
        country: profile.country,
        team_size: profile.team_size,
        working_style: profile.working_style,
      };
      const { data } = await api.patch("/business/profile", payload);
      setOrg(data.organization);
      setProfile(data.organization.business_profile || {});
      if (ctx.setOrg) ctx.setOrg(data.organization);
      toast.success("Business profile updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSavingProfile(false);
    }
  };

  const saveContext = async (e) => {
    e.preventDefault();
    setSavingCtx(true);
    try {
      const { data } = await api.patch("/business/context", { business_context: context });
      setOrg(data.organization);
      if (ctx.setOrg) ctx.setOrg(data.organization);
      toast.success("Business context saved — your AI just got smarter.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSavingCtx(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/organizations/me", { name: name.trim(), default_currency: currency });
      setOrg(data.organization);
      if (ctx.setOrg) ctx.setOrg(data.organization);
      toast.success("Organization updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="org-settings">
      <h2 className="font-display text-xl font-semibold tracking-tight">Organization</h2>
      <p className="mt-1 text-sm text-muted-foreground">Your workspace details. Multi-member collaboration arrives in a future phase.</p>

      <form onSubmit={save} className="mt-6 space-y-4" data-testid="org-form">
        <div>
          <label className="mb-1.5 block text-sm font-medium">Workspace name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="org-name-input" disabled={!isOwner} required />
          {!isOwner && <p className="mt-1 text-xs text-muted-foreground">Only the owner can rename this workspace.</p>}
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium">Default currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              disabled={!isOwner}
              className={inputCls}
              data-testid="org-currency-select"
            >
              <option value="USD">USD — US Dollar</option>
              <option value="EUR">EUR — Euro</option>
              <option value="INR">INR — Indian Rupee</option>
              <option value="AED">AED — UAE Dirham</option>
            </select>
            <p className="mt-1 text-xs text-muted-foreground">New deals pre-fill with this currency. Each deal can override.</p>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">Plan</label>
            <input value={(org?.plan || "free").toUpperCase()} disabled className={`${inputCls} opacity-60`} />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">Workspace ID</label>
            <input value={org?.org_id || ""} disabled className={`${inputCls} opacity-60`} />
          </div>
        </div>
        <button type="submit" disabled={saving || !isOwner} data-testid="org-save-btn" className="rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition-all hover:-translate-y-0.5 disabled:opacity-60">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </form>

      <div className="mt-8 rounded-xl border border-dashed border-border p-5">
        <p className="text-sm font-medium">Team members</p>
        <p className="mt-1 text-xs text-muted-foreground">Invite teammates and manage roles — coming as your team grows.</p>
      </div>

      <form onSubmit={saveProfile} className="mt-6 rounded-2xl border border-border bg-background/40 p-5" data-testid="business-profile-form">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-brand-teal">
            <Icon name="Briefcase" className="h-4 w-4" />
          </span>
          <div>
            <p className="text-sm font-semibold">Business profile</p>
            <p className="text-xs text-muted-foreground">The workspace time zone and entity type shape every AI briefing, follow-up date and reminder.</p>
          </div>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Time zone</label>
            <TimezoneSelect
              value={profile.timezone || ""}
              onChange={(v) => updateProfile({ timezone: v })}
              testId="org-timezone-select"
              disabled={!isOwner}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Entity type</label>
            <select
              value={profile.entity_type || ""}
              onChange={(e) => updateProfile({ entity_type: e.target.value })}
              disabled={!isOwner}
              className={inputCls}
              data-testid="org-entity-type-select"
            >
              <option value="">Choose one…</option>
              {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Industry</label>
            <input
              value={profile.industry || ""}
              onChange={(e) => updateProfile({ industry: e.target.value })}
              disabled={!isOwner}
              className={inputCls}
              data-testid="org-industry-input"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Country</label>
            <input
              value={profile.country || ""}
              onChange={(e) => updateProfile({ country: e.target.value })}
              disabled={!isOwner}
              className={inputCls}
              data-testid="org-country-input"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={savingProfile || !isOwner}
          data-testid="business-profile-save"
          className="mt-4 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition-all hover:-translate-y-0.5 disabled:opacity-60"
        >
          {savingProfile ? "Saving…" : "Save business profile"}
        </button>
      </form>

      <form onSubmit={saveContext} className="mt-6 rounded-2xl border border-border bg-background/40 p-5" data-testid="business-context-form">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg brand-gradient-bg text-white">
            <Icon name="Sparkles" className="h-4 w-4" />
          </span>
          <div>
            <p className="text-sm font-semibold">Business context (AI memory)</p>
            <p className="text-xs text-muted-foreground">Anything you add here helps your AI understand your business better.</p>
          </div>
        </div>
        <textarea
          rows={5}
          value={context}
          onChange={(e) => setContext(e.target.value)}
          data-testid="business-context-input"
          placeholder="e.g. Our ICP is early-stage SaaS founders in India. Main channel is LinkedIn. Q1 focus is activation…"
          className="mt-4 w-full resize-none rounded-xl border border-border bg-card px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary"
        />
        <button type="submit" disabled={savingCtx} data-testid="business-context-save" className="mt-3 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition-all hover:-translate-y-0.5 disabled:opacity-60">
          {savingCtx ? "Saving…" : "Save context"}
        </button>
      </form>
    </div>
  );
}
